import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { getClientProfile, saveClientProfile, clearClientProfile } from "@/lib/clientStore";
import useConsultantPWAManifest from "@/lib/useConsultantPWAManifest";
import { ChevronLeft, Loader2, Pencil, LogOut, User as UserIcon, Mail, Phone, MapPin, Car, FileText } from "lucide-react";
import { toast } from "sonner";

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <Icon className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        <div className="text-sm text-[#1E293B] mt-0.5 break-words">{value || <span className="text-slate-400">—</span>}</div>
      </div>
    </div>
  );
}

export default function ClientProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [consultant, setConsultant] = useState(null);
  const [loading, setLoading] = useState(true);

  useConsultantPWAManifest(slug, consultant);

  useEffect(() => {
    const local = getClientProfile();
    if (!local) {
      navigate(`/c/${slug}/onboarding`, { replace: true });
      return;
    }
    setProfile(local);

    // Refresh from backend if we have a token
    api.get(`/consultants/by-slug/${slug}`).then(({ data }) => setConsultant(data)).catch(() => {});
    api.get(`/auth/me`)
      .then(({ data }) => {
        const merged = { ...local, ...data };
        saveClientProfile(merged);
        setProfile(merged);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  const logout = () => {
    if (!window.confirm("Vuoi disconnetterti? Dovrai registrarti di nuovo per usare l'app.")) return;
    clearClientProfile();
    toast.success("Disconnesso");
    navigate(`/c/${slug}`, { replace: true });
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2456C6]" />
      </div>
    );
  }

  const primaryColor = consultant?.brand_color_primary || "#F77F2A";
  const fullAddress = [
    profile.address_street,
    [profile.address_postal_code, profile.address_city].filter(Boolean).join(" "),
    profile.address_province,
  ].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0F172A] text-white safe-top">
        <div className="max-w-md mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => navigate(`/c/${slug}`)} data-testid="profile-back-button"
            className="p-2 -ml-2 hover:bg-white/10 rounded-lg text-white/80">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0 text-center">
            {consultant?.logo_url ? (
              <img src={fileUrl(consultant.logo_url)} alt={consultant.studio_name}
                className="h-10 mx-auto bg-white rounded-lg p-1 object-contain" style={{ maxWidth: 160 }} />
            ) : (
              <div className="font-black">Profilo</div>
            )}
          </div>
          <div className="w-9" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 py-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center font-black text-white text-xl shrink-0"
            style={{ background: primaryColor }}>
            {(profile.first_name?.[0] || "C")}{(profile.last_name?.[0] || "")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xl font-black text-[#1E293B] truncate" data-testid="profile-name">
              {profile.first_name} {profile.last_name}
            </div>
            <div className="text-xs text-slate-500">Cliente di {consultant?.studio_name || "—"}</div>
          </div>
        </div>

        <div className="card-gs !p-5 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">I tuoi dati</div>
            <Link to={`/c/${slug}/onboarding`} data-testid="edit-profile-link"
              className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: primaryColor }}>
              <Pencil className="w-3.5 h-3.5" /> Modifica
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            <Field icon={Mail} label="Email" value={profile.email} />
            <Field icon={Phone} label="Telefono" value={profile.phone} />
            <Field icon={MapPin} label="Indirizzo" value={fullAddress} />
            <Field icon={Car} label="Targa veicolo" value={profile.vehicle_plate} />
            {profile.fiscal_code && <Field icon={FileText} label="Codice fiscale" value={profile.fiscal_code} />}
          </div>
        </div>

        <div className="card-gs !p-4">
          <div className="text-xs text-slate-500 mb-3 leading-relaxed">
            I tuoi dati sono salvati sul tuo dispositivo e sul tuo profilo cliente presso <strong>{consultant?.studio_name || "il tuo consulente"}</strong>.
            Trattamento secondo Privacy Policy.
          </div>
          <button onClick={logout} data-testid="logout-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-rose-100 text-rose-700 font-bold py-3 rounded-xl text-sm transition">
            <LogOut className="w-4 h-4" /> Disconnetti questo dispositivo
          </button>
        </div>
      </main>
    </div>
  );
}
